/**
 * Écran de secours quand l'application ne démarre pas (SAAS-898).
 *
 * @remarks
 * `main.tsx` enchaîne des étapes qui peuvent échouer avant que React soit monté :
 * variables d'env invalides, conflit détecté par le registry des features
 * (`markInitialized` échoue volontairement, SAAS-884), import dynamique en
 * échec, `#root` introuvable. Sans traitement, la promesse rejetait dans le vide
 * et l'utilisateur restait devant une page blanche.
 *
 * Le `RootErrorBoundary` ne peut rien ici : il vit dans React, qui n'a pas
 * démarré. D'où un écran construit avec l'API DOM seule.
 *
 * En prod, un message générique ; en dev, le message de l'erreur en plus — c'est
 * là qu'un « conflit de route » du registry est utile à lire.
 *
 * Le message est posé en `textContent`, jamais en HTML : il peut porter du texte
 * venu d'ailleurs (SAAS-899).
 */

import { reloadPage } from './router/chunk-load-error';

interface BootFailureOptions {
  /** Où afficher l'écran. Par défaut `#root`, ou `<body>` s'il manque — c'est un des cas d'échec. */
  readonly container?: HTMLElement;
  /** Affiche le message de l'erreur. Par défaut en dev seulement. */
  readonly showDetails?: boolean;
  readonly reload?: () => void;
}

/** Remplace le contenu du conteneur par l'écran de secours. */
export function renderBootFailure(error: unknown, options: BootFailureOptions = {}): void {
  // La console est le seul relais tant qu'aucun service de log n'est branché (SAAS-895).
  console.error('[boot] Échec du démarrage :', error);

  const container = options.container ?? document.getElementById('root') ?? document.body;
  const showDetails = options.showDetails ?? import.meta.env.DEV;
  const reload = options.reload ?? reloadPage;

  const screen = document.createElement('div');
  screen.setAttribute('role', 'alert');
  screen.className =
    'bg-bg text-fg flex min-h-screen flex-col items-center justify-center gap-4 p-8';

  const title = document.createElement('h1');
  title.className = 'text-2xl font-semibold';
  title.textContent = "L'application n'a pas pu démarrer";

  const hint = document.createElement('p');
  hint.className = 'text-fg-muted max-w-md text-center';
  hint.textContent = 'Recharge la page. Si le problème persiste, contacte le support.';

  screen.append(title, hint);

  if (showDetails) {
    const details = document.createElement('pre');
    details.className = 'bg-bg-muted text-fg-muted max-w-2xl overflow-auto rounded-sm p-4 text-xs';
    details.textContent = error instanceof Error ? error.message : String(error);
    screen.append(details);
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'bg-primary-500 hover:bg-primary-600 rounded-md px-4 py-2 text-white';
  button.textContent = 'Recharger';
  button.addEventListener('click', () => {
    reload();
  });
  screen.append(button);

  container.replaceChildren(screen);
}
